import React, { Component } from 'react'
import { graphql } from 'gatsby'
import Img from 'gatsby-image'
import SEO from '../../../components/seo'

import axios from 'axios'

import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'

import Layout from '../../../components/layout/layout'
import NextPreviousRow from '../../../components/next-previous'
import PageTitle from '../../../components/page-title'

import isUserSignedIn from '../../../auth/is-user-signed-in'

import StudentContent from '../../../components/layout/student-content'
import TeacherContentStudentAccordion from '../../../components/layout/teacher-content/teacher-content-student-accordion'
import TeacherContentTeacherAccordion from '../../../components/layout/teacher-content/teacher-content-teacher-accordion'

// eslint-disable-next-line
import threedmssImage from '../../../queries/threedmssImage'

const Page = class extends Component {
  constructor(props) {
    super(props)

    this.state = {
      student_accordion_open: true,
      authenticated: undefined
    }

    this.axios_cancel_token = axios.CancelToken.source()
  }

  async componentDidMount() {
    const is_signed_in = isUserSignedIn()
    this.setState({ authenticated: is_signed_in })
  }

  render() {
    const { location } = this.props
    if (this.state.authenticated === undefined) {
      return (
        <React.Fragment>
          <Layout>
            <Container fluid className="d-flex h-100 flex-column pt-3">
              <Row className="mb-3 justify-content-center w-100">
                <div className="spinner-grow text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </Row>
            </Container>
          </Layout>
        </React.Fragment>
      )
    } else {
      return (
        <React.Fragment>
          <SEO title="M'Kenna's Absorption Problem" />
          <Layout
            location={location}
            course="threedmss"
            setStudentAccordionState={(state) => this.setState({ student_accordion_open: state })}
            setAuthenticated={(is_signed_in) => this.setState({ authenticated: is_signed_in })}
          >
            <Container fluid className="d-flex h-100 flex-column pt-3">
              <PageTitle title="M'Kenna's Absorption Problem" no_breadcrumb={true} />
              <Row style={{ marginBottom: '2rem' }}>
                {this.state.authenticated &&
                  <React.Fragment>
                    <Col xs={12} lg={6} id="student-accordion-column">
                      <TeacherContentStudentAccordion
                        title="Student View"
                        open={this.state.student_accordion_open}
                        setStudentAccordionState={() => {
                          /*
                            Anytime this is called, it's toggling so I'm just setting it to the opposite of its current state. This is to sync the student accordion state with the ToggleStudentView component.  Pretty much just don't worry about it. Keep walking.
                          */
                          if (this.state.student_accordion_open) {
                            this.setState({ student_accordion_open: false })
                          } else {
                            this.setState({ student_accordion_open: true })
                          }
                        }}
                      >
                        <div className="row">
                          <div className="col-12">
                            <hr className="focus-question-heading bold-me d-none d-md-block d-lg-none d-xl-block" />
                            <h3 className="bold-me d-block d-md-none d-lg-block d-xl-none text-center">Focus Question</h3>
                            <hr className="d-block d-md-none d-lg-block d-xl-none" />
                            <p className="focus-question bold-me">How are M'Kenna's cells in the small intestine affecting how she absorbs food nutrients?</p>
                            <hr className="after-focus-question" />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-12">
                            <div className="image-wrapper">
                              <Img
                                fluid={this.props.data.image1.childImageSharp.fluid}
                                alt="Unhealthy intestinal tissue"
                                className="image"
                              />
                              <div className="image-caption">
                                M'Kenna's micrograph shows that her intestinal tissue does not look healthy. <br />
                                <span className="image-credit">Courtesy Biophoto Associates/Science Photo Library</span>
                              </div>
                            </div>

                            <p>M'Kenna returned to her doctor to hear the results of her exam. The doctor showed M'Kenna the micrograph images from her small intestine. The doctor said M'Kenna's small intestine looked like it had been mowed with a lawn mower. M'Kenna didn't like the sound of that!</p>

                            <p>The evidence from the micrographs suggests that M'Kenna's problem in the small intestine is happening on a microscopic scale. When you zoom in to look closely at the tissue in her small intestine, it looks different than healthy tissue. Now that you know a little more about how intestinal tissue absorbs nutrients, you will revisit some of the evidence about M'Kenna's absorption problem.</p>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-12">
                            <hr className="notebook-icon my-5" />
                            <span className="sr-only">Notebook Icon</span>
                            <h3 className="section-heading bold-me my-4">Set up your notebook with the focus question. Answer the question and write what you notice about M'Kenna.</h3>
                          </div>
                        </div>

                        <p className="list-heading bold-me underline-me">On your own</p>
                        <ol>
                          <li>In your science notebook, write the focus question, <span className="bold-me">How are M'Kenna's cells in the small intestine affecting how she absorbs food nutrients?</span> Draw a box around the question. Then write your ideas answering this question.</li>
                        </ol>
                      </TeacherContentStudentAccordion>
                    </Col>
                    <Col xs={12} lg={6} name="teacher-accordion-column">
                      <Container fluid>
                        <Row>
                          <TeacherContentTeacherAccordion title="Time Estimate" open={true}>
                            <p>Estimated time to complete this web page: 5 min</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Where are we in the storyline?" open={true}>
                            <p className="bold-me">Storyline for Disciplinary Core Ideas (DCIs)</p>

                            <p>Students have created fairly detailed models of the digestive system and figured out the relationship between surface area of tissue and the ability of that tissue to absorb food nutrients. They have also figured out that large molecules break down chemically when they move through the digestive system. In this lesson, they apply their learning to explain why M'Kenna's small intestine tissue affects her ability to absorb nutrients.</p>

                            <p className="bold-me">Storyline for Science and Engineering Practices (SEPs)</p>

                            <p>Students use models to explain why M'Kenna is not absorbing nutrients like a healthy person does. They practice and apply what they have done in previous lessons (using models to explain).</p>

                            <p className="bold-me">Storyline Related to Crosscutting Concepts (CCCs)</p>

                            <p>Students have been using structure and function in the chapter. Now they move from structure and function to cause and effect. Namely, if the structure of an organ is such that it cannot function, then that structure and lack of function are a cause of an illness (effect).</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Phenomenon for This Lesson" open={true}>
                            <p>M'Kenna's symptoms show that small molecules (glucose, amino acids, and fatty acids) are not absorbed by her small intestine.</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Main Learning Goal" open={true}>
                            <p>When cell and tissue structures, such as villi and microvilli, are damaged, the cell membrane can't function to absorb nutrients properly. <span className="italic">Students will meet this main learning goal by the end of this Elaborate</span>.</p>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Teacher Moves During Lesson" open={true}>
                            <ol type="A">
                              <li>Ask students to write their ideas to the focus question in <span className="bold-me">step 1</span>. They are likely to have many good ideas about how to answer this question. Let them know that they will be writing a complete explanation for why M'Kenna's tissue affects her absorption of nutrients in this lesson.</li>
                            </ol>
                          </TeacherContentTeacherAccordion>

                          <TeacherContentTeacherAccordion title="Opportunity for English Language Arts Connection">
                            <p>This lesson provides an opportunity to integrate English language arts with science. Students should be able to read and comprehend the science text on this page independently and proficiently.</p>
                            <p><a href="http://www.corestandards.org/ELA-Literacy/RST/6-8/10/" target="_blank" rel="noopener noreferrer">CCSS.ELA-Literacy.RST.6-8.10&nbsp;<sup><i style={{fontSize: '.65rem'}} className="fas fa-external-link-alt"/></sup></a></p>
                          </TeacherContentTeacherAccordion>
                        </Row>
                      </Container>
                    </Col>
                  </React.Fragment>
                }
                {!this.state.authenticated &&
                  <StudentContent>
                    <div className="row">
                      <div className="col-12">
                        <hr className="focus-question-heading bold-me" />
                        <p className="focus-question bold-me">How are M'Kenna's cells in the small intestine affecting how she absorbs food nutrients?</p>
                        <hr className="after-focus-question" />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <div className="image-wrapper image-wrapper-left">
                          <Img
                            fluid={this.props.data.image1.childImageSharp.fluid}
                            alt="Unhealthy intestinal tissue"
                            className="image"
                          />
                          <div className="image-caption">
                            M'Kenna's micrograph shows that her intestinal tissue does not look healthy. <br />
                            <span className="image-credit">Courtesy Biophoto Associates/Science Photo Library</span>
                          </div>
                        </div>

                        <p>M'Kenna returned to her doctor to hear the results of her exam. The doctor showed M'Kenna the micrograph images from her small intestine. The doctor said M'Kenna's small intestine looked like it had been mowed with a lawn mower. M'Kenna didn't like the sound of that!</p>

                        <p>The evidence from the micrographs suggests that M'Kenna's problem in the small intestine is happening on a microscopic scale. When you zoom in to look closely at the tissue in her small intestine, it looks different than healthy tissue. Now that you know a little more about how intestinal tissue absorbs nutrients, you will revisit some of the evidence about M'Kenna's absorption problem.</p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <hr className="notebook-icon my-5" />
                        <span className="sr-only">Notebook Icon</span>
                        <h3 className="section-heading bold-me my-4">Set up your notebook with the focus question. Answer the question and write what you notice about M'Kenna.</h3>
                      </div>
                    </div>

                    <p className="list-heading bold-me underline-me">On your own</p>
                    <ol>
                      <li>In your science notebook, write the focus question, <span className="bold-me">How are M'Kenna's cells in the small intestine affecting how she absorbs food nutrients?</span> Draw a box around the question. Then write your ideas answering this question.</li>
                    </ol>
                  </StudentContent>
                }
              </Row>
              <NextPreviousRow location={location} authenticated={this.state.authenticated} />
            </Container>
          </Layout>
        </React.Fragment>
      )
    }
  }
}

export default Page

export const query = graphql`
  query {
    image1: file(relativePath: { eq: "02_vil/1473.jpg" }) {
      ...threedmssImage
    }
  }
`
